import React, { useState, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    Container,
    Header,
    SpaceBetween,
    Button,
    Box,
    Tabs,
    Badge,
    Alert,
    Textarea,
} from '@cloudscape-design/components';
import Avatar from '@cloudscape-design/chat-components/avatar';
import LoadingBar from '@cloudscape-design/chat-components/loading-bar';

// ── Blockquote classifier ────────────────────────────────────────────────────
// The analyzer emits blockquotes directly after labels like `**Original**`,
// `**Suggested replacement**`, or under `## Update blurb`. ReactMarkdown gives
// us the raw child nodes of a blockquote, so we use the blockquote text itself
// as a minor disambiguator and primarily rely on pre-processing the markdown
// to annotate blockquotes with an HTML comment marker we can read back.
//
// Pre-processing approach: before passing markdown to ReactMarkdown, we
// insert an invisible zero-width-joiner prefix inside the blockquote text so
// we can detect it in the `blockquote` component override.
//
// Prefixes:
//   U+200B U+0001  → original
//   U+200B U+0002  → suggested
//   U+200B U+0003  → blurb

const PREFIX_ORIGINAL  = '\u200B\u0001';
const PREFIX_SUGGESTED = '\u200B\u0002';
const PREFIX_BLURB     = '\u200B\u0003';

function annotateMarkdown(md) {
    if (!md) return md;

    const lines = md.split('\n');
    const out = [];
    let mode = null;           // 'original' | 'suggested' | 'blurb' | null
    let inBlurbSection = false;

    const labelRe = /^\s*\*\*(Original|Suggested replacement|Update blurb)\*\*\s*$/i;
    const headingRe = /^##\s+/;
    const blurbHeadingRe = /^##\s+Update blurb\s*$/i;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Track Update blurb section (the entire section's leading blockquote
        // should be styled as a blurb)
        if (blurbHeadingRe.test(line)) {
            inBlurbSection = true;
            out.push(line);
            continue;
        }
        if (inBlurbSection && headingRe.test(line) && !blurbHeadingRe.test(line)) {
            inBlurbSection = false;
        }

        // Inline labels take precedence over the section-level blurb mode
        const m = line.match(labelRe);
        if (m) {
            const l = m[1].toLowerCase();
            if (l === 'original') mode = 'original';
            else if (l.startsWith('suggested')) mode = 'suggested';
            else if (l.startsWith('update blurb')) mode = 'blurb';
            out.push(line);
            continue;
        }

        // If this line starts a blockquote and we have a pending mode, tag it
        if (/^\s*>/.test(line)) {
            let effectiveMode = mode;
            if (!effectiveMode && inBlurbSection) effectiveMode = 'blurb';
            if (effectiveMode) {
                const prefix =
                    effectiveMode === 'original'  ? PREFIX_ORIGINAL  :
                    effectiveMode === 'suggested' ? PREFIX_SUGGESTED :
                    effectiveMode === 'blurb'     ? PREFIX_BLURB     : '';
                // Insert prefix right after ">"
                const annotated = line.replace(/^(\s*>\s?)/, `$1${prefix}`);
                out.push(annotated);
                // Consume mode on the first blockquote line only so that
                // consecutive `>` lines remain in the same blockquote/style
                // without re-matching.
                continue;
            }
        }

        // A blank or non-blockquote line ends the current mode
        if (mode && !/^\s*>/.test(line)) {
            mode = null;
        }

        out.push(line);
    }
    return out.join('\n');
}

function classifyBlockquoteFromChildren(children) {
    // Walk children text nodes to find our prefix marker
    const collect = (nodes) => {
        let s = '';
        React.Children.forEach(nodes, (c) => {
            if (typeof c === 'string') s += c;
            else if (c?.props?.children) s += collect(c.props.children);
        });
        return s;
    };
    const text = collect(children);
    if (text.includes(PREFIX_ORIGINAL))  return 'original';
    if (text.includes(PREFIX_SUGGESTED)) return 'suggested';
    if (text.includes(PREFIX_BLURB))     return 'blurb';
    return null;
}

// Recursively strip our invisible marker characters from children so they
// don't render as stray glyphs.
function stripMarkers(children) {
    return React.Children.map(children, (c) => {
        if (typeof c === 'string') {
            return c.replace(/[\u200B\u0001\u0002\u0003]/g, '');
        }
        if (React.isValidElement(c) && c.props?.children) {
            return React.cloneElement(c, {}, stripMarkers(c.props.children));
        }
        return c;
    });
}

// ── Markdown renderer ────────────────────────────────────────────────────────
const MarkdownDoc = ({ content }) => {
    const annotated = useMemo(() => annotateMarkdown(content), [content]);

    return (
        <div className="bc-markdown">
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    h1: ({ node, ...props }) => <h1 className="bc-h1" {...props} />,
                    h2: ({ node, ...props }) => <h2 className="bc-h2" {...props} />,
                    h3: ({ node, ...props }) => <h3 className="bc-h3" {...props} />,
                    h4: ({ node, ...props }) => <h4 className="bc-h4" {...props} />,
                    p:  ({ node, ...props }) => <p  className="bc-p"  {...props} />,
                    ul: ({ node, ...props }) => <ul className="bc-ul" {...props} />,
                    ol: ({ node, ...props }) => <ol className="bc-ol" {...props} />,
                    li: ({ node, ...props }) => <li className="bc-li" {...props} />,
                    strong: ({ node, ...props }) => <strong className="bc-strong" {...props} />,
                    a: ({ node, href, children, ...props }) => (
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bc-link"
                            {...props}
                        >
                            {children}
                        </a>
                    ),
                    code: ({ node, inline, ...props }) =>
                        inline
                            ? <code className="bc-inline-code" {...props} />
                            : <code className="bc-code-block" {...props} />,
                    pre: ({ node, ...props }) => <pre className="bc-pre" {...props} />,
                    hr:  ({ node, ...props }) => <hr className="bc-hr" {...props} />,
                    blockquote: ({ node, children, ...props }) => {
                        const kind = classifyBlockquoteFromChildren(children);
                        const cls = kind
                            ? `bc-blockquote bc-blockquote-${kind}`
                            : 'bc-blockquote';
                        return (
                            <blockquote className={cls} {...props}>
                                {stripMarkers(children)}
                            </blockquote>
                        );
                    },
                }}
            >
                {annotated}
            </ReactMarkdown>
        </div>
    );
};

// ── Copy + Download buttons ──────────────────────────────────────────────────
const DocActions = ({ text, filename, copyLabel = 'Copy', downloadLabel = 'Download .md' }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        });
    }, [text]);

    const handleDownload = useCallback(() => {
        const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    }, [text, filename]);

    return (
        <SpaceBetween direction="horizontal" size="xs">
            <Button variant="normal" iconName={copied ? 'check' : 'copy'} onClick={handleCopy}>
                {copied ? 'Copied!' : copyLabel}
            </Button>
            <Button variant="normal" iconName="download" onClick={handleDownload}>
                {downloadLabel}
            </Button>
        </SpaceBetween>
    );
};

// ── Metadata header ──────────────────────────────────────────────────────────
const MetaRow = ({ label, value }) => {
    if (!value) return null;
    const display = value === 'N/A' ? <em style={{ color: 'var(--color-text-body-secondary)' }}>unknown</em> : value;
    return (
        <Box fontSize="body-s">
            <span style={{ color: 'var(--color-text-label)', fontWeight: 600, marginRight: 6 }}>{label}:</span>
            <span style={{ color: 'var(--color-text-body-secondary)' }}>{display}</span>
        </Box>
    );
};

// ── Summary panel — change %, recommendation badge ──────────────────────────
const SummaryPanel = ({
    changePercent,
    recommendation,
    changeThreshold = 30,
    suggestionCount,
}) => {
    if (changePercent == null && !recommendation) return null;

    const pct = Math.max(0, Math.min(100, Number(changePercent) || 0));
    const isCreateNew = (recommendation || '').toUpperCase() === 'CREATE_NEW_BLOG';
    const recClass = isCreateNew ? 'bc-rec-create-new' : 'bc-rec-update';
    const recLabel = isCreateNew ? 'Create new blog' : 'Update in place';

    return (
        <div className="bc-summary-panel">
            <div className="bc-change-bar">
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#24292f',
                }}>
                    <span>Change percentage</span>
                    <span>{pct}%</span>
                </div>
                <div className="bc-change-track">
                    <div className="bc-change-fill" style={{ width: `${pct}%` }} />
                    <div className="bc-change-threshold" style={{ left: `${changeThreshold}%` }} title={`Threshold: ${changeThreshold}%`} />
                </div>
                <div className="bc-change-labels">
                    <span>0%</span>
                    <span>Threshold: {changeThreshold}%</span>
                    <span>100%</span>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
                <Box fontSize="body-s" color="text-body-secondary">Recommendation</Box>
                <span className={`bc-rec-badge ${recClass}`}>{recLabel}</span>
            </div>

            {Number.isFinite(suggestionCount) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
                    <Box fontSize="body-s" color="text-body-secondary">Suggestions</Box>
                    <Badge color={suggestionCount > 0 ? 'blue' : 'green'}>
                        {suggestionCount} inline
                    </Badge>
                </div>
            )}

        </div>
    );
};

// ── Single result card ───────────────────────────────────────────────────────
const ResultCard = ({ result, isStreaming, streamingDoc, changeThreshold }) => {
    const doc = isStreaming ? streamingDoc : result.report_md;
    const [activeTab, setActiveTab] = useState('rendered');

    const scoreColor = result.score >= 0.8 ? 'green'
        : result.score >= 0.6 ? 'blue'
        : result.score != null ? 'red'
        : 'grey';

    return (
        <Container
            header={
                <Header
                    variant="h3"
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            {result.cached && (
                                <Badge color="grey">
                                    {result.cached_stored_at
                                        ? `Cached (${new Date(result.cached_stored_at).toLocaleDateString()})`
                                        : 'Cached'}
                                </Badge>
                            )}
                            {result.score != null && (
                                <Badge color={scoreColor}>
                                    Score: {result.score.toFixed(2)}
                                </Badge>
                            )}
                            {result.iterations != null && (
                                <Badge color="grey">
                                    {result.iterations} iter{result.iterations !== 1 ? 's' : ''}
                                </Badge>
                            )}
                            {result.arc_count > 0 && (
                                <Badge color="blue">
                                    {result.arc_count} revision{result.arc_count !== 1 ? 's' : ''}
                                </Badge>
                            )}
                            {doc && (
                                <DocActions
                                    text={doc}
                                    filename={`${(result.title || 'blog-review').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-review.md`}
                                />
                            )}
                        </SpaceBetween>
                    }
                >
                    {result.title}
                </Header>
            }
        >
            {isStreaming && (
                <Box padding={{ bottom: 's' }}>
                    <SpaceBetween direction="horizontal" size="xs">
                        <Avatar
                            ariaLabel="Blog Checker"
                            color="gen-ai"
                            iconName="gen-ai"
                            tooltipText="Reviewing…"
                        />
                        <LoadingBar variant="gen-ai" />
                    </SpaceBetween>
                </Box>
            )}

            {result.url && (
                <div className="bc-meta-block">
                    <SpaceBetween direction="vertical" size="xxs">
                        <Box fontSize="body-s">
                            <span className="bc-meta-label">URL:</span>{' '}
                            <a href={result.url} target="_blank" rel="noopener noreferrer" className="bc-link">
                                {result.url}
                            </a>
                        </Box>
                        <MetaRow label="Authors"   value={result.authors} />
                        <MetaRow label="Published" value={result.date} />
                        <MetaRow label="Services"  value={result.services} />
                    </SpaceBetween>
                </div>
            )}

            {(result.change_percent != null || result.recommendation) && (
                <SummaryPanel
                    changePercent={result.change_percent}
                    recommendation={result.recommendation}
                    changeThreshold={changeThreshold}
                    suggestionCount={result.suggestion_count}
                />
            )}

            {doc ? (
                <Tabs
                    activeTabId={activeTab}
                    onChange={({ detail }) => setActiveTab(detail.activeTabId)}
                    tabs={[
                        {
                            id: 'rendered',
                            label: 'Rendered',
                            content: <MarkdownDoc content={doc} />,
                        },
                        {
                            id: 'raw',
                            label: 'Raw Markdown',
                            content: (
                                <div style={{ position: 'relative' }}>
                                    <Textarea
                                        value={doc}
                                        readOnly
                                        rows={Math.min(40, doc.split('\n').length + 2)}
                                        spellcheck={false}
                                    />
                                </div>
                            ),
                        },
                    ]}
                />
            ) : result.error ? (
                <Alert type="error">{result.error}</Alert>
            ) : (
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    <SpaceBetween direction="vertical" size="s" alignItems="center">
                        <LoadingBar variant="gen-ai" />
                        <Box color="text-body-secondary" fontSize="body-s">
                            Pipeline running — the review report will appear here when ready
                        </Box>
                    </SpaceBetween>
                </Box>
            )}
        </Container>
    );
};

// ── Main viewer ──────────────────────────────────────────────────────────────
export default function BlogReportViewer({
    results,
    streamingDocs,
    selectedResultIdx,
    setSelectedResultIdx,
    changeThreshold = 30,
}) {
    const allDocs = results.map(r => r.report_md).filter(Boolean).join('\n\n---\n\n');

    return (
        <SpaceBetween direction="vertical" size="m">
            {results.length > 1 && (
                <Container
                    header={
                        <Header
                            variant="h2"
                            actions={
                                allDocs && (
                                    <DocActions
                                        text={allDocs}
                                        filename="blog-reviews-all.md"
                                        copyLabel="Copy All"
                                        downloadLabel="Download All"
                                    />
                                )
                            }
                        >
                            Blog review reports ({results.length})
                        </Header>
                    }
                >
                    <Tabs
                        activeTabId={String(selectedResultIdx ?? 0)}
                        onChange={({ detail }) => setSelectedResultIdx(Number(detail.activeTabId))}
                        tabs={results.map((r, i) => ({
                            id:    String(i),
                            label: (r.title || r.url || 'Result').length > 40
                                ? (r.title || r.url || 'Result').slice(0, 38) + '…'
                                : (r.title || r.url || 'Result'),
                        }))}
                    />
                </Container>
            )}

            {results.length === 1 && (
                <Header variant="h2">Blog review report</Header>
            )}

            {results.length > 0 && (() => {
                const idx    = selectedResultIdx ?? 0;
                const result = results[Math.min(idx, results.length - 1)];
                if (!result) return null;
                const streaming = streamingDocs[result.jobId] || '';
                const isStreaming = result.status === 'pending' && !!streaming;
                return (
                    <ResultCard
                        result={result}
                        isStreaming={isStreaming}
                        streamingDoc={streaming}
                        changeThreshold={changeThreshold}
                    />
                );
            })()}
        </SpaceBetween>
    );
}
